import React, { useState, useEffect } from 'react';
import { SettingsErrorBoundary } from '../components';
import { Button, Input } from '@/shared';
import { useForm } from 'react-hook-form';
import type { UpdateOrganizationSettingsData } from '../types';
import {
  useOrganizationSettings,
  useUpdateOrganizationSettings,
} from '../hooks/useOrganizationSettings';
import { organizationApi } from '../api/organizationApi'; // Ensure this path is correct
import toast from 'react-hot-toast';
import { useManagersQuery, useDeleteManager } from '../../houses/hooks';
import { DeleteManagerModal } from '@/features/houses/components/modals';

interface TabItem {
  id: string;
  label: string;
  component: React.ComponentType;
}

const SettingsPageContent: React.FC = () => {
  const [activeTab, setActiveTab] = useState('profile');

  // Fetch organization settings
  const {
    data: organizationData,
    isLoading,
    error,
  } = useOrganizationSettings();
  const updateSettings = useUpdateOrganizationSettings();

  // Initialize form
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<UpdateOrganizationSettingsData>({
    mode: 'onChange',
  });

  // Populate form with fetched data
  useEffect(() => {
    if (organizationData) {
      reset({
        name: organizationData.name || '',
        phoneNumber: organizationData.phoneNumber || '',
        address: organizationData.address || '',
        addressLine2: organizationData.addressLine2 || '',
        city: organizationData.city || '',
        postalCode: organizationData.postalCode || '',
        country: organizationData.country || '',
        email: organizationData.email || '',
        website: organizationData.website || '',
        registrationNumber: organizationData.registrationNumber || '',
        currency: organizationData.currency || '',
      });
    }
  }, [organizationData, reset]);

  // Handle form submission
  const onSubmit = (data: UpdateOrganizationSettingsData) => {
    updateSettings.mutate(data);
  };

  // Define available tabs
  const tabs: TabItem[] = [
    {
      id: 'profile',
      label: 'Profile',
      component: () => (
        <GeneralSettingsTab
          register={register}
          errors={errors}
          isLoading={isLoading}
          isSubmitting={isSubmitting}
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          organizationLogo={organizationData?.logo}
        />
      ),
    },
    {
      id: 'address',
      label: 'Address',
      component: () => (
        <AddressSettingsTab
          register={register}
          errors={errors}
          isLoading={isLoading}
          isSubmitting={isSubmitting}
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          setValue={setValue}
        />
      ),
    },
    {
      id: 'paymentAndBilling',
      label: 'Payment & Billing',
      component: () => (
        <BillingSettingsTab
          register={register}
          errors={errors}
          isLoading={isLoading}
          isSubmitting={isSubmitting}
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
        />
      ),
    },
    {
      id: 'security',
      label: 'Security & Access',
      component: SecuritySettingsTab,
    },
    {
      id: 'legalAndDocs',
      label: 'Compliance & Legal',
      component: LegalSettingsTab,
    },
  ];

  const activeTabData = tabs.find((tab) => tab.id === activeTab);
  const ActiveComponent = activeTabData?.component || tabs[0].component;

  if (error) {
    return (
      <div className="min-h-screen bg-background w-full px-0 sm:px-6 lg:px-8">
        <div className="p-6 text-center">
          <p className="text-red-500 text-xl">
            Failed to load organization settings. Please try again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background w-full px-0 sm:px-6 lg:px-8">
      {/* Tabs  */}
      <div className="flex items-center gap-4 mb-4 md:mb-8 overflow-x-auto lg:overflow-visible lg:flex-wrap pb-2 lg:pb-0 scrollbar-hide">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`whitespace-nowrap flex-shrink-0 px-4 py-2 rounded-lg transition-all duration-200 ${
              activeTab === tab.id
                ? 'bg-[#5761EB] text-white font-bold shadow-md'
                : 'text-text-primary hover:bg-gray-100'
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {/* Tab container */}

      <div className="py-2 md:p-6">
        <div
          role="tabpanel"
          className="lg:w-[50%]"
          aria-labelledby={`${activeTab}-tab`}
        >
          <ActiveComponent />
        </div>
      </div>
    </div>
  );
};

// Tabs components
interface FormTabProps {
  register: ReturnType<
    typeof useForm<UpdateOrganizationSettingsData>
  >['register'];
  errors: ReturnType<
    typeof useForm<UpdateOrganizationSettingsData>
  >['formState']['errors'];
  isLoading: boolean;
  isSubmitting: boolean;
  handleSubmit: ReturnType<
    typeof useForm<UpdateOrganizationSettingsData>
  >['handleSubmit'];
  onSubmit: (data: UpdateOrganizationSettingsData) => void;
  setValue?: ReturnType<
    typeof useForm<UpdateOrganizationSettingsData>
  >['setValue'];
}

const GeneralSettingsTab: React.FC<
  FormTabProps & { organizationLogo?: string }
> = ({
  register,
  errors,
  isLoading,
  isSubmitting,
  handleSubmit,
  onSubmit,
  organizationLogo,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    organizationLogo || null,
  );
  const [uploadStatus, setUploadStatus] = useState<
    'idle' | 'uploading' | 'success' | 'error'
  >('idle');
  const [uploadedLogoPath, setUploadedLogoPath] = useState<string | null>(null);

  useEffect(() => {
    if (organizationLogo) {
      setPreviewUrl(organizationLogo);
    }
  }, [organizationLogo]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      // setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      handleUpload(file);
    }
  };

  const handleUpload = async (file: File) => {
    try {
      setUploadStatus('uploading');

      // Create a unique but consistent filename to overwrite previous uploads
      // preserving the original extension
      const fileExtension = file.name.split('.').pop() || 'png';
      const fileName = `organization-profile.${fileExtension}`;

      const { signedUrl, path } = await organizationApi.getUploadUrl(
        fileName,
        file.type,
      );

      await fetch(signedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      setUploadedLogoPath(path);
      setUploadStatus('success');
      toast.success('Profile picture uploaded successfully');
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadStatus('error');
      toast.error('Failed to upload profile picture');
    }
  };

  const onFormSubmit = (data: UpdateOrganizationSettingsData) => {
    if (uploadedLogoPath) {
      data.logo = uploadedLogoPath;
    }
    onSubmit(data);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5761EB]"></div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="w-50 h-50 mb-4 relative">
        <img
          src={previewUrl || '/assets/images/houses/house-1.png'}
          alt="Profile"
          className="w-full h-full object-cover rounded-lg"
        />
        {uploadStatus === 'uploading' && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          </div>
        )}
      </div>
      <div className="mb-4 md:mb-6">
        <label
          htmlFor="setting-profile-selection"
          className="text-base md:text-lg text-[#5761EB] cursor-pointer hover:text-[#4048bf] transition-colors"
        >
          {uploadStatus === 'uploading'
            ? 'Uploading...'
            : 'Upload profile picture'}
        </label>
        <input
          type="file"
          className="hidden"
          id="setting-profile-selection"
          onChange={handleFileSelect}
          accept="image/*"
          disabled={uploadStatus === 'uploading'}
        />
      </div>
      <div>
        <form className="space-y-4 md:space-y-6" onSubmit={handleSubmit(onFormSubmit)}>
          {/* Hidden input for logo if we decide to manage it via form */}
          <div>
            <Input
              label="Organization Name"
              className="!text-base md:!text-xl"
              {...register('name')}
              error={errors.name?.message}
              disabled={isSubmitting}
            />
          </div>
          <div>
            <Input
              label="Registration number"
              placeholder="123A 45X67"
              className="!text-base md:!text-xl"
              {...register('registrationNumber')}
              error={errors.registrationNumber?.message}
              disabled={isSubmitting}
            />
          </div>
          <div>
            <Input
              label="Organization email"
              placeholder="contact@soberhouse.com"
              className="!text-base md:!text-xl"
              {...register('email')}
              error={errors.email?.message}
              disabled={isSubmitting}
            />
          </div>
          <div>
            <Input
              label="Organization number"
              placeholder="+1 (312) 456-7890"
              className="!text-base md:!text-xl"
              {...register('phoneNumber')}
              error={errors.phoneNumber?.message}
              disabled={isSubmitting}
            />
          </div>
          <div>
            <Input
              label="Website"
              placeholder="https://www.soberhouse.com"
              className="!text-base md:!text-xl"
              {...register('website')}
              error={errors.website?.message}
              disabled={isSubmitting}
            />
          </div>

          {/* Submit Button */}
          <div className="md:pt-4">
            <Button
              type="submit"
              className="!text-base md:!text-lg !font-bold cursor-pointer"
              disabled={isSubmitting || uploadStatus === 'uploading'}
            >
              {isSubmitting ? 'Saving...' : 'Save changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddressSettingsTab: React.FC<FormTabProps> = ({
  register,
  errors,
  isLoading,
  isSubmitting,
  handleSubmit,
  onSubmit,
  setValue,
}) => {
  // Function to reverse geocode coordinates
  const reverseGeocode = async (lat: number, lng: number) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
      );
      const data = await response.json();

      if (data.address && setValue) {
        // Construct a more complete address string
        const parts = [];
        if (data.address.house_number) parts.push(data.address.house_number);
        if (data.address.road) parts.push(data.address.road);
        if (data.address.neighbourhood) parts.push(data.address.neighbourhood);
        if (data.address.suburb) parts.push(data.address.suburb);

        const fullAddress = parts.join(', ');
        if (fullAddress) {
          setValue('address', fullAddress);
        } else if (data.display_name) {
          const displayNameParts = data.display_name.split(', ');
          setValue('address', displayNameParts.slice(0, 3).join(', '));
        }

        // Set city (try town, city, village, or county)
        if (
          data.address.town ||
          data.address.city ||
          data.address.village ||
          data.address.county
        ) {
          setValue(
            'city',
            data.address.town ||
              data.address.city ||
              data.address.village ||
              data.address.county,
          );
        }

        // Set country (use actual country field, not state)
        if (data.address.country) {
          setValue('country', data.address.country);
        }

        // Set postal code
        if (data.address.postcode) {
          setValue('postalCode', data.address.postcode);
        }
      }
    } catch (error) {
      console.error('Error reverse geocoding:', error);
    }
  };

  const handleCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          reverseGeocode(latitude, longitude);
        },
        (error) => {
          console.error('Error getting location:', error);
        },
      );
    } else {
      console.error('Geolocation is not supported by this browser.');
    }
  };
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5761EB]"></div>
      </div>
    );
  }

  return (
    <div className="">
      <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
        {/* Organization Name */}
        <div>
          <div className="flex items-center mb-4 relative">
            <Input
              label="Head office location"
              placeholder="Suite 204, Maple Avenue, Greenfield City"
              className="!text-base md:!text-xl"
              {...register('address')}
              error={errors.address?.message}
              disabled={isSubmitting}
            />
            <div
              className="absolute top-2/3 right-2 -translate-y-1/2 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={handleCurrentLocation}
              title="Use current location"
            >
              <img src="/assets/icons/maps.svg" alt="Get current location" />
            </div>
          </div>
          <Input
            placeholder="Line 2 (optional)"
            className="!text-base md:!text-xl"
            {...register('addressLine2')}
            error={errors.addressLine2?.message}
            disabled={isSubmitting}
          />
        </div>
        <div>
          <Input
            label="City"
            placeholder="Enter city"
            className="!text-base md:!text-xl"
            {...register('city')}
            error={errors.city?.message}
            disabled={isSubmitting}
          />
        </div>
        <div>
          <Input
            label="Postal code"
            placeholder="Postal code"
            className="!text-base md:!text-xl"
            {...register('postalCode')}
            error={errors.postalCode?.message}
            disabled={isSubmitting}
          />
        </div>
        <div>
          <Input
            label="Country"
            placeholder="Country"
            className="!text-base md:!text-xl"
            {...register('country')}
            error={errors.country?.message}
            disabled={isSubmitting}
          />
        </div>

        {/* Submit Button */}
        <div className="md:pt-4">
          <Button
            type="submit"
            className="!text-base md:!text-lg !font-bold cursor-pointer"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save changes'}
          </Button>
        </div>
      </form>
    </div>
  );
};

const BillingSettingsTab: React.FC<FormTabProps> = ({
  register,
  errors,
  isLoading,
  isSubmitting,
  handleSubmit,
  onSubmit,
}) => {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5761EB]"></div>
      </div>
    );
  }

  return (
    <div className="">
      <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <Input
            label="Default currency"
            placeholder="USD"
            className="!text-base md:!text-xl"
            {...register('currency')}
            error={errors.currency?.message}
            disabled={isSubmitting}
          />
        </div>

        {/* Submit Button */}
        <div className="md:pt-4">
          <Button
            type="submit"
            className="!text-base md:!text-lg !font-bold cursor-pointer"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save changes'}
          </Button>
        </div>
      </form>
    </div>
  );
};

const SecuritySettingsTab: React.FC = () => {
  const { data: managersData, isLoading } = useManagersQuery({
    page: 1,
    limit: 100, // Fetch all managers (or a reasonable limit)
    search: '',
    status: '',
    assignedHouses: '',
    location: '',
  });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedManager, setSelectedManager] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [deleteManagerErrors, setDeleteManagerErrors] = useState<string[]>([]);

  const deleteManager = useDeleteManager();

  const handleRemoveManager = (managerId: string, managerName: string) => {
    setSelectedManager({ id: managerId, name: managerName });
    setIsDeleteModalOpen(true);
    setDeleteManagerErrors([]);
  };

  const handleConfirmDelete = (managerId: string) => {
    deleteManager.mutate(
      { managerId },
      {
        onSuccess: () => {
          toast.success('Manager removed successfully');
          setIsDeleteModalOpen(false);
          setSelectedManager(null);
        },
        onError: (error: unknown) => {
          const err = error as {
            response?: { data?: { message?: string } };
            message?: string;
          };
          const errorMessage =
            err.response?.data?.message ||
            err.message ||
            'Failed to remove manager';
          setDeleteManagerErrors([errorMessage]);
          toast.error(errorMessage);
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5761EB]"></div>
      </div>
    );
  }

  const managers = managersData?.managers || [];

  return (
    <div className="">
      <h3 className="text-text-primary font-bold text-lg md:text-xl mb-4">
        Manage house managers
      </h3>
      <div>
        {managers.length === 0 ? (
          <p className="text-text-secondary">No managers found.</p>
        ) : (
          managers.map((manager) => (
            <div key={manager.id} className="p-3 mb-3 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-text-primary text-lg md:text-xl mb-2">
                    {manager.name}
                  </p>
                  <p className="text-text-secondary">{manager.email}</p>
                </div>
                <div>
                  <button
                    onClick={() =>
                      handleRemoveManager(manager.id, manager.name)
                    }
                    className="text-[#891518] text-base md:text-lg font-bold p-3 hover:bg-red-50 rounded-lg transition-colors"
                    disabled={deleteManager.isPending}
                  >
                    {deleteManager.isPending ? 'Removing...' : 'Remove'}
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Delete Modal */}
      {selectedManager && (
        <DeleteManagerModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleConfirmDelete}
          manager={selectedManager}
          errors={deleteManagerErrors}
        />
      )}
    </div>
  );
};

// Resource Imports
import { resourceApi, type Resource } from '../../resources/api/resourceApi';
import { DeleteResourceModal } from '../../resources/components/modals/DeleteResourceModal';

const LegalSettingsTab: React.FC = () => {
  const [certificates, setCertificates] = useState<Resource[]>([]);
  const [terms, setTerms] = useState<Resource[]>([]);
  // const [isLoading, setIsLoading] = useState(true);
  const [uploadingType, setUploadingType] = useState<
    'CERTIFICATE' | 'LEGAL_DOCUMENT' | null
  >(null);

  // Delete Modal State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [resourceToDelete, setResourceToDelete] = useState<Resource | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchResources = async () => {
    try {
      // setIsLoading(true);
      // Fetch Certificates
      const certsData = await resourceApi.getResources(
        1,
        100,
        '',
        'CERTIFICATE',
      );
      setCertificates(certsData.resources);

      // Fetch Terms
      const termsData = await resourceApi.getResources(
        1,
        100,
        '',
        'LEGAL_DOCUMENT',
      );
      setTerms(termsData.resources);
    } catch (error) {
      console.error('Failed to fetch legal documents:', error);
      toast.error('Failed to load documents');
    } finally {
      // setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchResources();
  }, []);

  const handleUpload = async (
    file: File,
    type: 'CERTIFICATE' | 'LEGAL_DOCUMENT',
  ) => {
    try {
      setUploadingType(type);

      // 1. Get Signed URL
      const { signedUrl, path } = await resourceApi.getUploadUrl(
        file.name,
        'resources', // Module name for storage path organization/{orgId}/resources/{filename}
        file.type,
      );

      // 2. Upload to Storage
      const uploadResponse = await fetch(signedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed with status: ${uploadResponse.status}`);
      }

      // 3. Create Resource Record
      await resourceApi.createResource({
        title: file.name,
        description:
          type === 'CERTIFICATE'
            ? 'Business License / Certificate'
            : 'Terms & Conditions',
        category: type,
        fileUrl: path,
        fileType: file.type,
      });

      toast.success('Document uploaded successfully');
      fetchResources(); // Refresh list
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploadingType(null);
    }
  };

  const handleDeleteClick = (resource: Resource) => {
    setResourceToDelete(resource);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async (resourceId: string) => {
    try {
      setIsDeleting(true);
      await resourceApi.deleteResource(resourceId);
      toast.success('Document removed successfully');
      fetchResources();
      setIsDeleteModalOpen(false);
      setResourceToDelete(null);
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to remove document');
    } finally {
      setIsDeleting(false);
    }
  };

  const renderDocumentSection = (
    title: string,
    type: 'CERTIFICATE' | 'LEGAL_DOCUMENT',
    resources: Resource[],
  ) => (
    <div className="mb-3">
      <div className="mb-6">
        <p className="text-text-primary text-xl">{title}</p>
      </div>

      {/* Upload Area */}
      <div className="mb-3">
        <input
          type="file"
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" // Accept common doc types
          className="hidden"
          id={`file-upload-${type}`}
          onChange={(e) => {
            if (e.target.files?.[0]) {
              handleUpload(e.target.files[0], type);
            }
          }}
          disabled={!!uploadingType}
        />
        <label htmlFor={`file-upload-${type}`}>
          <div
            className={`border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer transition-colors ${
              uploadingType === type ? 'bg-gray-50' : 'hover:bg-gray-50'
            }`}
          >
            <div className="flex flex-col gap-4 items-center justify-center">
              {uploadingType === type ? (
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 13.5V3"
                    stroke="#141414"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M20.25 13.5V19.5H3.75V13.5"
                    stroke="#141414"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8.25 6.75L12 3L15.75 6.75"
                    stroke="#141414"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              <p className="text-sm">
                {uploadingType === type
                  ? 'Uploading...'
                  : 'Click to choose a file or drag here'}
              </p>
              <p className="text-xs text-gray-500 mt-2">Size limit: 10MB</p>
            </div>
          </div>
        </label>
      </div>

      {/* Resource List */}
      <div className="space-y-3">
        {resources.map((resource) => (
          <div
            key={resource.id}
            className="rounded-lg p-3 border border-[#BFBFBF]"
          >
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-0">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-[#E53935] rounded-lg">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M19.0005 12.333C17.5838 10.833 13.7505 11.4997 12.8338 11.583C11.5005 10.2497 10.5838 8.66634 10.2505 8.16634C10.7505 6.66634 11.0838 5.24967 11.0838 3.66634C11.0838 2.33301 10.5005 0.833008 9.00051 0.833008C7.41718 0.833008 7.33385 2.74968 7.33385 3.49968C7.41718 4.58301 7.75051 5.99967 8.33385 7.33301C7.75051 8.99967 7.16718 10.6663 5.66718 13.4997C4.66718 13.9163 3.00051 14.6663 1.83385 15.583C0.917182 16.2497 -0.166152 17.4163 1.00052 18.6663C1.91718 19.583 3.33385 19.083 4.33385 18.333C5.58385 17.333 6.75052 15.583 7.66718 13.9997C8.83385 13.583 10.5838 13.083 12.4172 12.7497C14.5005 14.583 16.4172 14.833 17.3338 14.833C18.6672 14.833 19.0838 14.2497 19.2505 13.833C19.5838 13.4163 19.4172 12.7497 19.0005 12.333ZM17.6672 13.2497C17.5838 13.6663 17.0838 13.9997 16.2505 13.833C15.2505 13.583 14.3338 13.083 13.5005 12.4163C14.1672 12.333 15.7505 12.1663 16.9172 12.333C17.3338 12.4997 17.8338 12.7497 17.6672 13.2497ZM9.08385 1.83301C9.58385 1.83301 9.66718 2.41634 9.66718 2.91634C9.58385 4.08301 9.41718 5.16634 9.00051 6.24967C8.50051 4.83301 8.33385 3.66634 8.41718 2.91634C8.41718 2.58301 8.58385 1.83301 9.08385 1.83301ZM8.50052 12.6663C8.91718 11.833 9.50052 10.2497 9.75052 9.66634C10.2505 10.4997 11.0838 11.4163 11.5005 11.9163C11.5005 11.833 9.83385 12.1663 8.50052 12.6663ZM5.33385 14.833C2.75051 18.9997 1.83385 18.1663 1.75052 18.083C1.58385 17.9163 0.667182 16.9163 5.33385 14.833Z"
                      fill="white"
                    />
                  </svg>
                </div>
                <div>
                  <a
                    href={resource.fileUrl || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-text-primary text-base md:text-xl mb-2 hover:underline"
                  >
                    {resource.title}
                  </a>
                  <p className="text-text-secondary">
                    Upload date{' '}
                    {new Date(resource.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <div>
                <button
                  onClick={() => handleDeleteClick(resource)}
                  className="text-[#891518] text-base md:text-lg font-bold p-3"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="">
      {renderDocumentSection(
        'Upload business license / Certificates',
        'CERTIFICATE',
        certificates,
      )}
      {renderDocumentSection(
        'Terms & conditions',
        'LEGAL_DOCUMENT',
        terms,
      )}

      {resourceToDelete && (
        <DeleteResourceModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setResourceToDelete(null);
          }}
          onConfirm={handleConfirmDelete}
          resource={resourceToDelete}
          isLoading={isDeleting}
        />
      )}
    </div>
  );
};

const SettingsPage: React.FC = () => {
  return (
    <SettingsErrorBoundary onRetry={() => window.location.reload()}>
      <SettingsPageContent />
    </SettingsErrorBoundary>
  );
};

export default SettingsPage;
